import {rrulestr} from 'rrule'
import Tag from './Tag'
import TacticFieldValue from './TacticFieldValue'
import TacticPlatform from './TacticPlatform'
import ID from './ID'
import PlanDocument from './PlanDocument'
import isValidDate from '@/utils/isValidDate'
import { getExDates } from '@/utils/recurrence'
import { parseAllDayDate } from '@/utils/parseAllDayDate'
import RelatedInitiativeMapping from './RelatedInitiativeMapping'
import PlanningItem, { TacticSummaryData } from './InterfacePlanningItem'

export default class Tactic implements PlanningItem {
  public editing = false
  public opened = false
  public newTitle: string
  public newStartDate: Date
  public newEndDate: Date
  public newFile: any
  public newFileName: string
  public newSummary: string
  public tacticSummary: TacticSummaryData | null = null

  constructor(
    public id: ID = new ID(),
    public creatorId: ID = new ID(),
    public title: string = '',
    public startDate: Date = new Date(Date.now()),
    public endDate: Date = new Date(Date.now()),
    public tacticTypeId: ID = new ID(),
    public estimatedCost: number = 0,
    public actualCost: number = 0,
    public impressions: number = 0,
    public ratingPoints: number = 0,
    public tagIds: ID[] = [],
    public typeValues: TacticFieldValue[] = [],
    public relatedInitiatives: RelatedInitiativeMapping[] = [],
    public isFlagged: boolean = false,
    public planId: ID = new ID(),
    public isAllDay: boolean = false,
    public repeats: boolean = false,
    public recurrenceEndDate: Date | null = null,
    public isCustomRecurrence: boolean = false,
    public recurrenceText: string = '',
    public rrule?: string | null, // optional so we can delete it before inserting in calendar if null
    public tacticPlatformIds: ID[] = [],
    public isNested: boolean = false,
    public isLead: boolean = false,
    public documentIds: ID[] = [],
    // public localIsInitiative: boolean = false,
    public summary: string = '',
    public isUnscheduled: boolean = false,

    // public socialAliasNames: string[] = [], // Populated on front-end // Tactic alias system no longer used - JV 05.24.22
    public channelName: string = '', // Populated on front-end
    public tacticTypeName: string = '', // Populated on front-end
    public isSubscriptionEvent: boolean = false, // Populated on front-end
    public abbreviatedPlanName: string = '', // Populated on front-end
    public tags: Tag[] = [], // Populated on front-end
    public tacticPlatforms: TacticPlatform[] = [], // Populated on front-end
    public documents: PlanDocument[] = [], // Populated on front-end
    public originalTacticID: ID | null = null, // Populated on front-end
    public isRecurringEventClone: boolean = false, // Populated on front-end
  ) {
    this.newTitle = title
    this.newStartDate = startDate
    this.newEndDate = endDate
    this.newFile = null
    this.newFileName = ''
    this.newSummary = summary
  }

  public get key() {
    return 'tactic-' + this.id.intID.toString()
  }

  public get startDateString(): string {
    return this.startDate.toLocaleDateString()
  }

  public get endDateString(): string {
    return this.endDate.toLocaleDateString()
  }

  public get start(): Date {
    return this.startDate
  }

  public get end(): Date {
    if (this.isAllDay) {
      // Add one day to end date of all day events to resolve confusing issue (https://github.com/fullcalendar/fullcalendar/issues/2909)
      const modifiedDate = new Date(this.endDate.getTime())
      modifiedDate.setDate(modifiedDate.getDate() + 1)
      return modifiedDate
    }
    return this.endDate
  }

  public get allDay(): boolean {
    return this.isAllDay
  }

  public get tacticPlatformName(): string {
    let returnString = ''
    if (this.tacticPlatforms && this.tacticPlatforms.length) {
      this.tacticPlatforms.forEach((platform)=>{
        returnString += platform.name + ' '
      })
    }
    return returnString
  }

  public get isInitiative(): boolean {
    return false
    // if(this.localIsInitiative == true){
    //   return true
    // }
    // if(this.channelName !== '' && this.channelName === Channel.CHANNEL_NAME_INITIATIVES){
    //   return true
    // }
    // return false
  }

  // public set isInitiative(value: boolean) {
  //   this.localIsInitiative = value
  // }

  public get tacticPlatformNames() {
    return this.tacticPlatforms?.map((platform)=>platform.name).join(', ')
  }

  public get duration(): number | object {
    const datesDifference = this.endDate.getTime() - this.startDate.getTime()
    if(datesDifference === 0){
      // Return one day if start and end date are equal
      if(this.isAllDay){
        return 86400000 // full day
      }
      return 60000 // one hour
    }
    if(this.isAllDay){
      // Add one day to the difference for all day tactics to work around exact 24-hour difference equaling one day
      return {
        days: Math.ceil(datesDifference / 86400000) + 1
      }
    }
    return datesDifference
  }

  // EXDATE exception dates broken out of this tactic's recurrence string (read-only;
  // the rrule string remains the source of truth — mutate via utils/recurrence helpers).
  public get exDates(): Date[] {
    return this.rrule ? getExDates(this.rrule) : []
  }

  private getFormattedRecurranceString() {
    if(this.rrule){
      return this.rrule.replace('\nRRULE:',';').replace('DTSTART:', 'DTSTART=')
    }
    return ``
  }

  public static fromResponseObject(obj): Tactic {
    if (!obj) return new Tactic()

    const tagIds = [] as ID[]
    if (obj.tags) {
      obj.tags.forEach((element) => {
        tagIds.push(ID.fromResponseObject(element, 'tags'))
      })
    }

    const typeValues = [] as TacticFieldValue[]
    if (obj.typeValues) {
      obj.typeValues.forEach((element) => {
        typeValues.push(TacticFieldValue.fromResponseObject(element))
      })
    }

    let startDateString = obj.startDate
    if (obj.startDate && obj.startDate.date) {
      startDateString = obj.startDate.date
    }

    let endDateString = obj.endDate
    if (obj.endDate && obj.endDate.date) {
      endDateString = obj.endDate.date
    }

    // Handle recurring tactic properties
    let recurrenceEndDate: Date|null = null
    let recurrenceText = ''
    if(obj.recurrence){
      // Remove '\nRRULE:' from rrule in case legacy tactic data contains this malformatted strings
      obj.recurrence = obj.recurrence.replace('\nRRULE:',';')

      // Split off any EXDATE tail (recurrence-break exceptions) so the UNTIL surgery and the
      // recurrenceText parsing below operate on the RRULE body only. rrulestr returns an
      // RRuleSet (which has no toText()) when EXDATE is present, and the UNTIL string surgery
      // would otherwise corrupt the EXDATE line. The tail is re-appended afterward so the full
      // string still drives FullCalendar / rrulestr expansion (and hides excluded instances).
      let exDateTail = ''
      const exDateIndex = obj.recurrence.search(/\r?\nEXDATE/i)
      if (exDateIndex !== -1) {
        exDateTail = obj.recurrence.slice(exDateIndex)
        obj.recurrence = obj.recurrence.slice(0, exDateIndex)
      }

      // Set up recurrence end date
      if(obj.recurrenceEndDate){
        // Date-only strings (e.g. "2026-03-24") are parsed as UTC midnight by JS,
        // which shifts to the previous day in negative-offset timezones. Parse as local noon instead.
        const rawEndDate = obj.recurrenceEndDate
        if (typeof rawEndDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawEndDate)) {
          recurrenceEndDate = new Date(rawEndDate + 'T12:00:00')
        } else {
          recurrenceEndDate = new Date(rawEndDate)
        }

        // Set to end of local day so the UNTIL covers the full selected date,
        // ensuring instances scheduled on that day are included
        recurrenceEndDate.setHours(23, 59, 59, 999)

        // Build UNTIL string using local date components (consistent with TacticDetail.vue)
        const year = recurrenceEndDate.getFullYear()
        const month = String(recurrenceEndDate.getMonth() + 1).padStart(2, '0')
        const date = String(recurrenceEndDate.getDate()).padStart(2, '0')
        const newUntilString = `UNTIL=${year}${month}${date}T235959Z`

        // Apply UNTIL date to recurrence string
        const untilIndex = obj.recurrence?.indexOf('UNTIL=')
        if(untilIndex > -1){
          obj.recurrence = `${obj.recurrence.substring(0,untilIndex)}${newUntilString}`
        }else{
          obj.recurrence += `;${newUntilString}`
        }
      }

      if (obj.recurrence === 'custom') {
        // If the recurrence is accidentally set to 'custom', remove all recurrence properties
        obj.recurrence = null
        obj.isCustomRecurrence = false
        obj.repeats = false
        obj.recurrenceEndDate = null
        exDateTail = '' // drop exceptions along with the nulled recurrence
      }else if(obj.isCustomRecurrence && obj.recurrence !== ''){
        // Set the human-readable recurrence text (parsed from the RRULE body, sans EXDATE)
        try {
          const rule = rrulestr(obj.recurrence)
          const text = rule.toText().replace(/ until .*/i, '')
          recurrenceText = text.charAt(0).toUpperCase() + text.slice(1)
        } catch (error) {
          recurrenceText = 'Custom'
        }
      }

      // Re-attach the EXDATE exceptions so the persisted/displayed recurrence string is complete
      if (obj.recurrence && exDateTail) {
        obj.recurrence += exDateTail
      }
    }

    const tacticPlatformIds = [] as ID[]
    if(obj.tacticPlatforms){
      obj.tacticPlatforms.forEach((element) => {
        tacticPlatformIds.push(ID.fromResponseObject(element, 'tactic_platforms'))
      })
    }

    const documentIds = [] as ID[]
    if (obj.documents) {
      obj.documents.forEach((element) => {
        documentIds.push(ID.fromResponseObject(element, 'documents'))
      })
    }

    // Catch invalid rrule formatting
    if(obj.id != null && obj.recurrence){
      // Weed out subscription events that have null id values. A non-recurring tactic has no
      // rrule to validate — rrulestr('') throws "Invalid empty string", which this block would
      // otherwise catch and log as a spurious warning on every non-recurring tactic parsed.
      try {
        rrulestr(obj.recurrence)
      } catch (error) {
        if (['local', 'dev'].includes(process.env.VUE_APP_ENV ?? '')) {
          console.warn(`Tactic.ts: Parsing tactic "${obj.title}" (${obj.id}) rrule -`, error)
        }
        
        // Repair invalid UNTIL datetime strings in rrule values
        const errorMessage = typeof error === 'string' ? error : ((error as any).message ?? '')
        if (errorMessage.includes('Invalid UNTIL value')) {
          // Extract the invalid value from the end of the error message (after the last ": ")
          const colonIdx = errorMessage.lastIndexOf(': ')
          const invalidValue = colonIdx !== -1 ? errorMessage.slice(colonIdx + 2).trim() : ''
          const tIndex = invalidValue.indexOf('T')
          if (tIndex !== -1) {
            const datePart = invalidValue.slice(0, tIndex)
            const timePart = invalidValue.slice(tIndex + 1).replace('Z', '')
            // 4-char time (HHMM) is missing seconds — pad right: "1900" → "190000"
            // 5-char time (HMMSS) is missing hour leading zero — pad left: "85959" → "085959"
            const fixedTime = timePart.length <= 4 ? timePart.padEnd(6, '0') : timePart.padStart(6, '0')
            const fixedValue = `${datePart}T${fixedTime}Z`
            obj.recurrence = obj.recurrence.replace(invalidValue, fixedValue)
          } else {
            obj.recurrence = null
          }
        } else {
          obj.recurrence = null
        }
      }
    }

    // Set up relatedInitiatives array
    // let relatedInitiatives = obj.initiatives ? obj.initiatives : []
    // if(typeof obj.initiatives == 'string'){
    //   // Old JSON format
    //   relatedInitiatives = JSON.parse(obj.initiatives)
    // }
    // if(Array.isArray(relatedInitiatives) && relatedInitiatives.length){
    //   relatedInitiatives = relatedInitiatives.map((mappingObj) => RelatedInitiativeMapping.fromResponseObject(obj.id, mappingObj))
    // }else{
    //   relatedInitiatives = []
    // }
    const relatedInitiatives = obj.initiatives ? obj.initiatives.map((initiativeObj) => RelatedInitiativeMapping.fromResponseObject(obj.id, initiativeObj)) : []

    const tactic = new Tactic(
      ID.fromResponseObject(obj.id, 'tactics'),
      ID.fromResponseObject(obj.creator, 'users'),
      obj.title,
      obj.isAllDay ? parseAllDayDate(startDateString) : new Date(startDateString),
      obj.isAllDay ? parseAllDayDate(endDateString) : new Date(endDateString),
      ID.fromResponseObject(obj.tacticType, 'tactic_types'),
      obj.estimatedCost,
      obj.actualCost,
      obj.impressions,
      obj.ratingPoints,
      tagIds,
      typeValues,
      relatedInitiatives,
      obj.isFlagged,
      ID.fromResponseObject(obj.plan, 'plans'),
      obj.isAllDay,
      !!obj.recurrence,
      recurrenceEndDate,
      obj.isCustomRecurrence,
      recurrenceText,
      obj.recurrence && obj.recurrence !== '' ? obj.recurrence : null,
      tacticPlatformIds,
      obj.isNested ? obj.isNested : false,
      obj.isLead ? obj.isLead : false,
      documentIds,
      // obj.isInitiative,
      '',
      obj.isUnscheduled
    )
    tactic.tacticSummary = obj.tacticSummary ? { id: ID.fromResponseObject(obj.tacticSummary['@id'], 'tactic_summaries'), contentUrl: obj.tacticSummary.contentUrl } : null
    return tactic
  }

  public forRequestObject() {
    return {
      id: this.id.intID == 0 ? null : this.id.apiID,
      creator: this.creatorId.intID == 0 ? null : this.creatorId.apiID,
      title: this.title,
      startDate: isValidDate(this.startDate) ? this.startDate : new Date(Date.now()),
      endDate: isValidDate(this.endDate) ? this.endDate : new Date(Date.now()),
      tacticType: this.tacticTypeId.intID == 0 ? null : this.tacticTypeId.apiID,
      estimatedCost: this.estimatedCost,
      actualCost: this.actualCost,
      impressions: this.impressions,
      ratingPoints: this.ratingPoints,
      tags: this.tags.map((tag) => tag.id.apiID),
      typeValues: this.typeValues.map((value) => value.id.apiID),
      initiatives: this.relatedInitiatives.filter((mappingObj) => mappingObj.initiativeId.intID !== 0).map((mappingObj) => mappingObj.forRequestObject()),
      isFlagged: this.isFlagged,
      plan: this.planId.intID == 0 ? null : this.planId.apiID,
      isAllDay: this.isAllDay,
      recurrence: this.getFormattedRecurranceString().length > 0 ? this.getFormattedRecurranceString() : null,
      recurrenceEndDate: this.recurrenceEndDate,
      isCustomRecurrence: this.isCustomRecurrence,
      tacticPlatforms: this.tacticPlatforms?.map((platform) => platform.id.apiID),
      documents: this.documents.map((document) => document.id.apiID),
      // isInitiative: this.isInitiative,
      isUnscheduled: this.isUnscheduled ? true : false,
    }
  }
}
